// =========================
// SupportInboxPanel — BL-037 Story 37.1 (Admin Portal — Support Observation)
//
// Read-only visibility into the customer feedback/support submissions captured
// by Story 37.1: total count, category + status aggregates, and the most
// recent submissions. Reads through lib/admin:
//   listSupportRequests() → { total, recent, byCategory, byStatus }
//
// This is OBSERVATION, not a ticketing/reply system — there are no reply or
// status-mutation controls (admin status transitions are a Story 37.3
// capability). It exists so onboarding friction and silent churn signals are
// observable for the controlled cohort.
//
// Rendered as a tab inside AdminDashboard; route gating
// (ProtectedRoute > AdminRoute) happens above the shell, and
// admin-list-support-requests independently enforces the admin gate
// server-side (defense-in-depth).
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { listSupportRequests, updateSupportRequest } from '../../lib/admin';

// The single authorized next action per current status (Story 37.3 state
// machine): new → reviewing, reviewing → closed, closed → reviewing. Forbidden
// transitions (new → closed, closed → new) are simply never offered here and
// are also rejected server-side.
const NEXT_ACTION = {
  new:       { label: 'Start reviewing', to: 'reviewing' },
  reviewing: { label: 'Close',           to: 'closed' },
  closed:    { label: 'Reopen',          to: 'reviewing' },
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

// Human label for a stored category key.
function categoryLabel(key) {
  if (key === 'feature_request') return 'Feature Request';
  if (key === 'bug') return 'Bug';
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function SupportInboxPanel() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await listSupportRequests();
    if (loadError) {
      setError(loadError.message || 'Could not load support requests');
      setSummary(null);
    } else {
      setSummary(data || { total: 0, recent: [], byCategory: {}, byStatus: {} });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Single-request status transition (no bulk). Server enforces the state
  // machine; on success we reload so the list + aggregates reflect the change.
  const runTransition = useCallback(async (id, to) => {
    setActionError(null);
    setPendingId(id);
    const { error: updateError } = await updateSupportRequest({ id, status: to });
    setPendingId(null);
    if (updateError) {
      setActionError(updateError.message || 'Could not update the request status.');
      return;
    }
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-5)', color: 'var(--color-steel-lt)', fontSize: 'var(--text-base)' }}>
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-danger)',
          background: 'var(--color-danger-ghost)',
          color: 'var(--color-danger-lt)',
          fontSize: 'var(--text-base)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {error}
      </div>
    );
  }

  const total = summary?.total ?? 0;
  const recent = summary?.recent ?? [];
  const byCategory = summary?.byCategory ?? {};
  const categoryEntries = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      {actionError && (
        <div
          role="alert"
          style={{
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-danger)',
            background: 'var(--color-danger-ghost)',
            color: 'var(--color-danger-lt)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-semibold)',
          }}
        >
          {actionError}
        </div>
      )}

      {/* Total submissions */}
      <div className="card-stat" style={{ textAlign: 'left' }}>
        <span
          style={{
            display: 'block',
            fontSize: 'var(--text-sm)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-wide)',
            color: 'var(--color-steel)',
            marginBottom: 'var(--space-1)',
          }}
        >
          Total submissions
        </span>
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--weight-black)',
            color: 'var(--color-white)',
          }}
        >
          {total}
        </span>
      </div>

      {total === 0 ? (
        <div style={{ padding: 'var(--space-5)', color: 'var(--color-muted)', fontStyle: 'italic' }}>
          No submissions yet
        </div>
      ) : (
        <>
          {/* Category composition */}
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-bold)',
                color: 'var(--color-white)',
                margin: 0,
              }}
            >
              By category
            </h2>
            {categoryEntries.map(([key, n]) => (
              <div
                key={key}
                className="card"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-2) var(--space-4)',
                }}
              >
                <span style={{ color: 'var(--color-steel-lt)' }}>{categoryLabel(key)}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 'var(--weight-bold)',
                    color: 'var(--color-white)',
                  }}
                >
                  {n}
                </span>
              </div>
            ))}
          </div>

          {/* Recent submissions */}
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--weight-bold)',
                color: 'var(--color-white)',
                margin: 0,
              }}
            >
              Recent submissions
            </h2>
            {recent.map((row) => {
              const action = NEXT_ACTION[row.status];
              return (
                <div
                  key={row.id}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-4)',
                    flexWrap: 'wrap',
                    padding: 'var(--space-2) var(--space-4)',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                    <span style={{ fontWeight: 'var(--weight-bold)', color: 'var(--color-white)' }}>
                      {row.subject}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
                      {categoryLabel(row.category)} · {row.user_id} · {formatDate(row.created_at)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <span className="badge" aria-label={`Status: ${row.status}`}>{row.status}</span>
                    {action && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={pendingId === row.id}
                        onClick={() => runTransition(row.id, action.to)}
                      >
                        {pendingId === row.id ? '…' : action.label}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
